import ServiceTemplate from './service-page-template/service-page-template.jsx';
import { imagesMetadata } from '../../imagesMetadata.js';

const heroImageSrc = '/static/images/tv-mounting-wall-art/mentone-picture-hanging-01.webp';
const heroMeta = imagesMetadata[heroImageSrc];

const excludedImages = [
  heroImageSrc,
  // Add any other image paths to exclude here...
];

const tvMountingGalleryImages = Object.entries(imagesMetadata)
  .filter(([src]) => src.startsWith('/static/images/tv-mounting-wall-art/') && !excludedImages.includes(src))
  .map(([src, meta], index) => ({
    id: index + 1,
    src,
    alt: meta.alt,
    location: meta.location
  }));

export default function TVMounting() {
    const servicesData = {
        title: "TV Mounting & Wall Art Installation",
        subtitle: "Expert TV wall mounting with cable management and professional wall art & gallery installations.",
        metaTitle: "TV Wall Mounting Melbourne | Art & Mirror Hanging",
        metaDesc: "TV wall mounting in Melbourne with neat cable management, plus secure mirror, artwork and gallery wall hanging. Book a careful local installer.",
        canonicalPath: "/tv-mounting-wall-art",

        solutionsTitle: "Our TV & Wall Art Installation Services",
        solutionsIntro: "We combine technical expertise with artistic precision to deliver secure TV installations and beautifully displayed wall art across Melbourne homes and offices.",

        solutionsList: [
            { 
                emoji: "📺", 
                heading: "TV Wall Mounting", 
                text: "Secure mounting of all TV sizes on plaster, brick, or concrete walls with professional cable management for a clean finish." 
            },
            { 
                emoji: "🪞", 
                heading: "Heavy Mirrors & Artwork", 
                text: "Safe installation of large mirrors, framed art, and decorative pieces using appropriate weight-rated fixings." 
            },
            { 
                emoji: "🖼️", 
                heading: "Gallery Walls & Collections", 
                text: "Curated layout and precise hanging of multi-piece gallery walls with perfect alignment and spacing." 
            },
            { 
                emoji: "🔌", 
                heading: "Soundbars, Shelves & Media Setup", 
                text: "Mounting of soundbars, floating media shelves, and full entertainment centre setups with hidden cabling." 
            }
        ],

        serviceImage: {
            src: heroImageSrc,
            alt: heroMeta?.alt || 'Professional picture gallery on double brick house in Menton by Handyman In Melbourne'
        },

        extendedDetails: [
            {
                title: "Quality Craftsmanship & Trade Standards",
                body: "All installations follow manufacturer guidelines and Australian safety standards. We use correct anchors and brackets rated for the specific load and wall type."
            },
            {
                title: "Precision & Attention to Detail",
                body: "Everything is perfectly level and aligned. We take extra care with cable management and artistic spacing so your TV and wall art look professionally installed."
            },
            {
                title: "Safety First Approach",
                body: "Heavy TVs, mirrors, and large art pieces are securely fixed. We always check for electrical cables and use appropriate heavy-duty hardware."
            },
            {
                title: "Clean & Tidy Work Sites",
                body: "Minimal disruption and thorough cleanup. We remove all packaging, dust, and drilling debris, leaving your home spotless."
            }
        ],

        galleryImages: tvMountingGalleryImages,

        faqs: [
            {
                question: 'What size and weight TVs can you mount?',
                answer: 'We safely mount TVs from 32" up to 85"+, including heavy OLED and QLED models, using appropriate brackets and wall types.'
            },
            {
                question: 'Do you mount on different wall materials?',
                answer: 'Yes — stud walls, brick, and concrete. We use the correct anchors and always check for safety.'
            },
            {
                question: 'Is cable hiding or management included?',
                answer: 'Yes. We run and conceal cables neatly behind the wall or with covers for a clean finish.'
            },
            {
                question: 'Do you supply the TV brackets?',
                answer: 'Yes, we can supply and install the bracket. Just tell us the type you want — fixed, tiltable, or full motion. And we’ll recommend the best option for your TV size and viewing needs for an additional cost. Alternatively, you are welcome to supply your own bracket and we’ll install it.'
            },
        ],
    };

    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": servicesData.title,
        "serviceType": servicesData.title,
        "description": servicesData.metaDesc,
        "url": `https://handymaninmelbourne.au${servicesData.canonicalPath}`,
        "provider": {
          "@type": "LocalBusiness",
          "name": "Handyman In Melbourne",
          "url": "https://handymaninmelbourne.au/"
        },
        "areaServed": [
            "City of Melbourne VIC",
            "City of Port Phillip VIC",
            "City of Glen Eira VIC",
            "City of Stonnington VIC",
            "City of Boroondara VIC",
            "City of Whitehorse VIC",
            "City of Manningham VIC",
            "City of Monash VIC",
            "City of Kingston VIC",
            "City of Yarra VIC",
            "City of Moonee Valley VIC",
            "City of Darebin VIC",
            "City of Greater Dandenong VIC"
        ],
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "TV & Wall Art Installation Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "TV Wall Mounting"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Heavy Mirrors & Artwork Installation"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Gallery Walls & Collections"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Soundbars, Shelves & Media Setup"
                }
              }
            ]
          }
  };

    return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <ServiceTemplate {...servicesData} />
    </>
  );
}
